import React, { useRef, useEffect, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

/**
 * Get display name for a user object (supports various API shapes).
 */
function getUserDisplayName(user) {
  return (
    user.name ||
    (user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.email ||
        "Unknown")
  );
}

/**
 * Get initials for avatar fallback (supports various API shapes).
 */
function getUserInitials(user) {
  if (user.name) {
    return user.name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  }
  if (user.firstName && user.lastName) {
    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
  }
  if (user.email) {
    return user.email[0].toUpperCase();
  }
  return "U";
}

const sizeClasses = {
  sm: "h-6 w-6 text-[10px]",
  md: "h-7 w-7 text-xs",
  lg: "h-8 w-8 text-xs",
};

/** Negative margin for stacking (overlap amount) per size - applied only when not at row start */
const stackMarginBySize = {
  sm: "-ml-1.5",
  md: "-ml-2",
  lg: "-ml-2.5",
};

/**
 * Stacked assignee avatars: overlapping circles so all assignees are visible
 * in a compact row. Wraps on small screens so none are hidden.
 */
export function StackedAssigneeAvatars({
  users = [],
  size = "md",
  showNameForSingle = false,
  className,
}) {
  if (!users || users.length === 0) {
    return (
      <span className="text-sm text-muted-foreground">Unassigned</span>
    );
  }

  const sizeClass = sizeClasses[size];
  const containerRef = useRef(null);
  const [rowStartIndices, setRowStartIndices] = useState(new Set([0]));

  // Detect which avatars are at the start of a wrapped row so we can align left edges
  useEffect(() => {
    if (users.length <= 1) return;
    const container = containerRef.current;
    if (!container) return;

    const updateRowStarts = () => {
      const children = container.querySelectorAll("[data-avatar-index]");
      if (children.length === 0) return;
      const starts = new Set([0]);
      let prevTop = null;
      children.forEach((el, i) => {
        const rect = el.getBoundingClientRect();
        if (prevTop !== null && rect.top > prevTop + 2) {
          starts.add(i);
        }
        prevTop = rect.top;
      });
      setRowStartIndices(starts);
    };

    const raf = requestAnimationFrame(() => {
      updateRowStarts();
    });
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(updateRowStarts);
    });
    ro.observe(container);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [users.length]);

  if (users.length === 1 && showNameForSingle) {
    const user = users[0];
    return (
      <div className="flex items-center gap-2">
        <Avatar
          className={cn(
            sizeClass,
            "shrink-0 border-2 border-white dark:border-gray-900 shadow-sm hover:shadow-md transition-shadow cursor-default ring-2 ring-background"
          )}
        >
          <AvatarImage src={user.avatar} alt={getUserDisplayName(user)} className="object-cover" />
          <AvatarFallback className="text-xs font-medium text-white bg-primary">
            {getUserInitials(user)}
          </AvatarFallback>
        </Avatar>
        <span className="text-sm truncate min-w-0">
          {getUserDisplayName(user)}
        </span>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        "flex flex-wrap items-center max-w-full min-w-0",
        users.length > 1 && "gap-y-1",
        className
      )}
    >
      {users.map((user, index) => (
        <div
          key={user.id}
          data-avatar-index={index}
          className={cn(
            "relative group shrink-0",
            users.length > 1 && !rowStartIndices.has(index) && stackMarginBySize[size]
          )}
        >
          <Avatar
            className={cn(
              sizeClass,
              "border-2 border-white dark:border-gray-900 shadow-sm hover:shadow-md transition-shadow cursor-default ring-2 ring-background"
            )}
          >
            <AvatarImage
              src={user.avatar}
              alt={getUserDisplayName(user)}
              className="object-cover"
            />
            <AvatarFallback className="font-medium text-white bg-primary">
              {getUserInitials(user)}
            </AvatarFallback>
          </Avatar>
          {/* Tooltip */}
          <div
            className={cn(
              "absolute bottom-full mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10",
              index === 0 ? "left-0" : "left-1/2 -translate-x-1/2"
            )}
          >
            {getUserDisplayName(user)}
            <div
              className={cn(
                "absolute top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900",
                index === 0 ? "left-2" : "left-1/2 -translate-x-1/2"
              )}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
